"use client";

import {
  Banknote,
  Boxes,
  Database,
  ExternalLink,
  LayoutDashboard,
  LogOut,
  Megaphone,
  Menu,
  Moon,
  PackageCheck,
  Store,
  Sun,
  Tag,
  FolderTree,
  UsersRound,
  X
} from "lucide-react";
import Link from "next/link";
import { usePathname, useRouter } from "next/navigation";
import { signOut } from "next-auth/react";
import { useTheme } from "next-themes";
import React, { memo, useCallback, useEffect, useMemo, useState } from "react";

type AdminUser = {
  name?: string | null;
  email?: string | null;
  image?: string | null;
  role?: string;
};

type NavigationLink = {
  href: string;
  label: string;
  icon: React.ElementType;
  exact?: boolean;
  prefetch?: boolean;
};

const navigationLinks: NavigationLink[] = [
  { href: "", label: "Overview", icon: LayoutDashboard, exact: true, prefetch: true },
  { href: "/products", label: "Products & Inventory", icon: Boxes, prefetch: false },
  { href: "/promotions", label: "Promotions & Banners", icon: Megaphone, prefetch: true },
  { href: "/categories", label: "Categories", icon: FolderTree, prefetch: true },
  { href: "/navigation", label: "Header Navigation", icon: Menu, prefetch: true },
  { href: "/orders", label: "Orders & Shipments", icon: PackageCheck, prefetch: false },
  { href: "/users", label: "Users & Customers", icon: UsersRound, prefetch: false },
  { href: "/coupons", label: "Coupons", icon: Tag, prefetch: true },
  { href: "/vendors", label: "Vendors", icon: Store, prefetch: false },
  { href: "/vendor-types", label: "Vendor Types", icon: Database, prefetch: false },
  { href: "/vendor-withdrawals", label: "Withdrawals", icon: Banknote, prefetch: false }
];

// Memoized Top Header - Does NOT re-render on navigation
const AdminHeader = memo(function AdminHeader({
  adminName,
  adminEmail,
  userInitials,
  sidebarOpen,
  onToggleSidebar,
  theme,
  setTheme,
  mounted,
  adminBase
}: {
  adminName: string;
  adminEmail: string;
  userInitials: string;
  sidebarOpen: boolean;
  onToggleSidebar: () => void;
  theme?: string;
  setTheme: (theme: "light" | "dark" | "system") => void;
  mounted: boolean;
  adminBase: string;
}) {
  return (
    <header className="sticky top-0 z-40 flex h-16 w-full items-center justify-between border-b border-white/10 bg-amazon-navy px-4 text-white shadow-sticky sm:px-6">
      <div className="flex items-center gap-3 sm:gap-4">
        <button
          type="button"
          onClick={onToggleSidebar}
          className="rounded p-1.5 hover:bg-white/10 lg:hidden"
          aria-label="Toggle admin sidebar"
        >
          {sidebarOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
        </button>

        <Link href={adminBase} prefetch={true} className="flex items-center gap-2">
          <span className="text-xl font-black tracking-wider text-white sm:text-2xl">MOVA</span>
          <span className="rounded bg-amazon-orange px-2 py-0.5 text-[11px] font-black uppercase text-slate-950 shadow-sm">
            Seller Central
          </span>
        </Link>
      </div>

      <div className="flex items-center gap-2 sm:gap-4">
        <Link
          href="/"
          target="_blank"
          className="flex items-center gap-1.5 rounded-md border border-white/20 bg-white/5 px-2.5 py-1.5 text-xs font-bold text-white transition hover:border-white hover:bg-white/10"
          title="Open customer storefront in a new tab"
        >
          <Store className="h-4 w-4 text-amazon-gold" />
          <span className="hidden sm:inline">View Store</span>
          <ExternalLink className="h-3 w-3 text-slate-300" />
        </Link>

        <button
          type="button"
          onClick={() => setTheme(theme === "dark" ? "light" : "dark")}
          className="rounded-md border border-transparent p-2 text-slate-200 hover:border-white/20 hover:bg-white/10"
          aria-label="Toggle theme"
        >
          {mounted && theme === "dark" ? <Sun className="h-4 w-4 text-amazon-gold" /> : <Moon className="h-4 w-4" />}
        </button>

        <div className="flex items-center gap-2 border-l border-white/15 pl-2 sm:pl-3">
          <div className="flex h-8 w-8 items-center justify-center rounded-full bg-amazon-orange text-xs font-black text-slate-950 shadow">
            {userInitials}
          </div>
          <div className="hidden flex-col text-left text-xs md:flex">
            <span className="font-bold leading-tight text-white">{adminName}</span>
            <span className="text-[11px] text-slate-300">{adminEmail}</span>
          </div>
          <button
            type="button"
            onClick={() => signOut({ callbackUrl: "/login" })}
            className="ml-1 rounded p-1.5 text-slate-300 hover:bg-white/10 hover:text-white"
            title="Sign Out of Admin Console"
          >
            <LogOut className="h-4 w-4" />
          </button>
        </div>
      </div>
    </header>
  );
});

// Memoized Nav Item - Only re-renders when active changes
const AdminNavItem = memo(function AdminNavItem({
  link,
  active,
  onNavigate,
  onPrefetch
}: {
  link: NavigationLink;
  active: boolean;
  onNavigate: (href: string) => void;
  onPrefetch: (href: string) => void;
}) {
  const Icon = link.icon;

  return (
    <Link
      href={link.href}
      prefetch={link.prefetch}
      onClick={() => onNavigate(link.href)}
      onMouseEnter={() => onPrefetch(link.href)}
      onPointerDown={() => onPrefetch(link.href)}
      onFocus={() => onPrefetch(link.href)}
      className={`flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-bold transition whitespace-nowrap ${
        active
          ? "bg-amazon-orange/10 text-amazon-orange shadow-sm dark:bg-amazon-orange/20"
          : "text-slate-600 hover:bg-slate-100 dark:text-slate-300 dark:hover:bg-white/5"
      }`}
    >
      <Icon className={`h-4 w-4 shrink-0 ${active ? "text-amazon-orange" : "text-slate-400"}`} />
      <span className="truncate">{link.label}</span>
    </Link>
  );
});


export function AdminLayoutClient({
  children,
  user
}: {
  children: React.ReactNode;
  user?: AdminUser;
}) {
  const pathname = usePathname();
  const router = useRouter();
  const adminBase = pathname?.startsWith("/admin1") ? "/admin1" : "/admin";
  const links = useMemo(
    () => navigationLinks.map((l) => ({ ...l, href: `${adminBase}${l.href}` })),
    [adminBase]
  );
  const { theme, setTheme } = useTheme();
  const [mounted, setMounted] = useState(false);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [optimisticPath, setOptimisticPath] = useState<string | null>(null);

  useEffect(() => setMounted(true), []);

  // Sync optimistic path when real pathname completes
  useEffect(() => {
    setOptimisticPath(null);
  }, [pathname]);

  const activePath = optimisticPath ?? pathname;

  const adminName = user?.name || "Admin";
  const adminEmail = user?.email || "admin@movamarket.com";
  const userInitials = useMemo(() => {
    return (adminName || "A")
      .split(" ")
      .map((part) => part[0])
      .join("")
      .slice(0, 2)
      .toUpperCase();
  }, [adminName]);

  const isLinkActive = useCallback(
    (href: string, exact?: boolean) => {
      if (!activePath) return false;
      if (exact) return activePath === href;
      return activePath.startsWith(href);
    },
    [activePath]
  );

  const handleToggleSidebar = useCallback(() => {
    setSidebarOpen((v) => !v);
  }, []);

  const handleNavigate = useCallback((href: string) => {
    setOptimisticPath(href);
    setSidebarOpen(false);
  }, []);

  const handlePrefetch = useCallback(
    (href: string) => {
      router.prefetch(href);
    },
    [router]
  );

  return (
    <div className="flex min-h-screen flex-col bg-slate-100 text-slate-900 dark:bg-slate-950 dark:text-slate-100">
      {/* Top Admin Header */}
      <AdminHeader
        adminName={adminName}
        adminEmail={adminEmail}
        userInitials={userInitials}
        sidebarOpen={sidebarOpen}
        onToggleSidebar={handleToggleSidebar}
        theme={theme}
        setTheme={setTheme}
        mounted={mounted}
        adminBase={adminBase}
      />

      {/* Main Admin Body */}
      <div className="flex flex-1 min-w-0">
        {/* Mobile Sidebar Overlay */}
        {sidebarOpen && (
          <div
            className="fixed inset-0 z-40 bg-black/50 backdrop-blur-sm lg:hidden"
            onClick={() => setSidebarOpen(false)}
          />
        )}

        {/* Fixed Sidebar */}
        <aside
          className={`fixed top-16 bottom-0 left-0 z-50 w-64 border-r border-slate-200 bg-white transition-transform duration-200 ease-in-out dark:border-white/10 dark:bg-slate-900 overflow-y-auto lg:z-30 ${
            sidebarOpen ? "translate-x-0" : "-translate-x-full lg:translate-x-0"
          }`}
        >
          <div className="flex min-h-full flex-col justify-between p-4">
            <div>
              <p className="px-3 py-2 text-[11px] font-black uppercase tracking-wider text-slate-400 dark:text-slate-500">
                Navigation
              </p>
              <nav className="space-y-1">
                {links.map((link) => {
                  const active = isLinkActive(link.href, link.exact);
                  return (
                    <AdminNavItem
                      key={link.href}
                      link={link}
                      active={active}
                      onNavigate={handleNavigate}
                      onPrefetch={handlePrefetch}
                    />
                  );
                })}
              </nav>
            </div>
          </div>
        </aside>

        {/* Content Wrapper offset by fixed sidebar on desktop */}
        <div className="flex flex-1 flex-col min-w-0 lg:pl-64">
          {/* Admin Page Content */}
          <main className="flex-1 p-4 sm:p-6 lg:p-8 min-w-0">
            <div className="mx-auto max-w-7xl">{children}</div>
          </main>

          {/* Admin Minimal Footer */}
          <footer className="border-t border-slate-200 bg-white py-4 text-center text-xs text-slate-500 dark:border-white/10 dark:bg-slate-900 dark:text-slate-400">
            <p>Mova Market Seller Central & Administrator Portal</p>
          </footer>
        </div>
      </div>
    </div>
  );
}
